/**
 * Where a scope's documents go in its repository, and how they read there
 * (git sync Stage 2, S2.4). Pure: no store, no network — scopeDocuments.js
 * does the reading.
 *
 *   documents/<name>-<id8>/_folder.yaml        a folder
 *   documents/<…>/<title>-<id8>.md             a document
 *   documents/<…>/<title>-<id8>/…              its subpages (a folder named after the page)
 *   documents/media/<id8>/<file>               its images and small videos
 *
 * `<id8>` is the first 8 characters of the id; names are slugs, so a rename
 * moves the file and history follows it by id (front matter `id:`).
 */
import { MEDIA_DIR } from './scopeRepoLayout';

/** Videos above this stay in Apiboo as a link (owner decision 02.10.2026). */
export const MAX_VIDEO_IN_GIT = 10 * 1024 * 1024;

export const id8 = (id) => String(id || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toLowerCase() || 'item';

/** "Release checklist (v2)" → "release-checklist-v2"; letters of every language stay. */
export function slugOf(name, fallback = 'untitled') {
  const s = String(name || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
  return s || fallback;
}

/**
 * Every node's place: folders → their `_folder.yaml`, documents → their `.md`
 * (+ the folder their subpages go in). A node whose parent is missing goes
 * to the top, never lost. `rootDirOf(node)` (card 4.5) puts a root elsewhere
 * than `documents/` — its workspace's directory; absent, nothing changes.
 * @param {Array<{id, type:'folder'|'document', name, parentId}>} nodes
 * @returns {Map<string, { file: string, dir: string }>}  dir = where its children go
 */
export function layoutDocuments(nodes, { rootDirOf = null } = {}) {
  const byId = new Map(nodes.map((n) => [String(n.id), n]));
  const out = new Map();
  const visiting = new Set();
  const place = (n) => {
    const id = String(n.id);
    if (out.has(id)) return out.get(id);
    let parentDir = null;
    const parent = n.parentId != null ? byId.get(String(n.parentId)) : null;
    if (parent && !visiting.has(id)) {
      visiting.add(id);
      parentDir = place(parent).dir;
      visiting.delete(id);
    }
    if (parentDir == null) parentDir = (rootDirOf && rootDirOf(n)) || 'documents';
    const base = `${slugOf(n.name, n.type === 'folder' ? 'folder' : 'untitled')}-${id8(id)}`;
    const dir = `${parentDir}/${base}`;
    const spot = n.type === 'folder' ? { file: `${dir}/_folder.yaml`, dir } : { file: `${dir}.md`, dir };
    out.set(id, spot);
    return spot;
  };
  nodes.forEach(place);
  return out;
}

/** A file name safe on every OS and in a Markdown link. */
export function safeFileName(name, fallback = 'file') {
  const s = String(name || '')
    .normalize('NFKC')
    .replace(/\p{Cc}+/gu, '')
    .replace(/[\\/:*?"<>|#%[\]()]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, 100);
  return s || fallback;
}

/** Media of one document → repository paths, one per node, names made unique within the document. */
export function mediaPathsFor(docId, imageNodes) {
  const taken = new Set();
  const out = new Map();
  for (const n of imageNodes) {
    const name = safeFileName(n.name, 'file');
    const dot = name.lastIndexOf('.');
    const [stem, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
    let candidate = name;
    for (let i = 2; taken.has(candidate.toLowerCase()); i += 1) candidate = `${stem}-${i}${ext}`;
    taken.add(candidate.toLowerCase());
    out.set(n, `${MEDIA_DIR}/${id8(docId)}/${candidate}`);
  }
  return out;
}

/** Does this file belong in git? Images always; videos up to MAX_VIDEO_IN_GIT; nothing without a download. */
export function mediaGoesToGit(node) {
  if (!node?.src) return false;
  if (String(node.mime || '').startsWith('video/')) return node.size > 0 && node.size <= MAX_VIDEO_IN_GIT;
  return true;
}

/** The link from a `.md` at `mdFile` to `target` (both repository paths): `../media/…`, as GitHub resolves it. */
export function relativeHref(mdFile, target) {
  const from = mdFile.split('/').slice(0, -1);
  const to = target.split('/');
  let i = 0;
  while (i < from.length && i < to.length - 1 && from[i] === to[i]) i += 1;
  return [...Array(from.length - i).fill('..'), ...to.slice(i)].join('/');
}

/** `href` in the `.md` at `mdFile` → the repository path it points to, or null if it leaves the repository. */
export function resolveHref(mdFile, href) {
  const h = String(href || '');
  if (!h || /^[a-z][a-z0-9+.-]*:/i.test(h) || /^[\\/]/.test(h) || h.includes('\\')) return null;
  const parts = mdFile.split('/').slice(0, -1);
  for (const seg of h.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (parts.length === 0) return null;
      parts.pop();
    } else parts.push(seg);
  }
  return parts.join('/');
}

/** Only links into documents/media/ are media of a document. */
export const isDocMediaHref = (mdFile) => (href) => {
  const p = resolveHref(mdFile, href);
  return Boolean(p && p.startsWith(`${MEDIA_DIR}/`));
};

const q = (v) => JSON.stringify(String(v ?? ''));

/** `scope:` + `scope_name:` for a workspace ROOT (card 4.5); nothing for anything else. */
const workspaceLines = (ws) => (ws ? [`scope: ${q(ws.id)}`, `scope_name: ${q(ws.name)}`] : []);

export const folderYaml = (node, ws = null) =>
  ['apiboo: folder', `id: ${q(node.id)}`, `name: ${q(node.name)}`, `order: ${q(node.sortKey)}`, ...workspaceLines(ws), ''].join('\n');

/**
 * A document's file: front matter + Markdown. `tags:` (card 2.3) is written
 * only when the document has tags, `scope:` / `scope_name:` (card 4.5) only on
 * a workspace's root — a tag-less Personal document's file is byte-identical
 * to what Stage 2 always wrote.
 */
export const documentFile = (node, markdown, ws = null) =>
  [
    '---',
    `id: ${q(node.id)}`,
    `title: ${q(node.name)}`,
    `order: ${q(node.sortKey)}`,
    ...workspaceLines(ws),
    ...(Array.isArray(node.tags) && node.tags.length ? [`tags: ${JSON.stringify(node.tags.map(String))}`] : []),
    '---',
    '',
    String(markdown || '').replace(/\n*$/, '\n'),
  ].join('\n');

/** The front matter's `tags:` value → a string list (absent or unreadable → []). */
export function tagsFromFrontMatter(raw) {
  if (raw == null || raw === '') return [];
  try {
    const list = JSON.parse(String(raw));
    return Array.isArray(list) ? list.filter((t) => typeof t === 'string' && t.trim()).map((t) => t.trim()) : [];
  } catch {
    return String(raw).split(',').map((t) => t.trim()).filter(Boolean);
  }
}

/* ── reading back (S2.4b) ──────────────────────────────────────────────── */

/** One `key: value` line as Apiboo writes it: JSON-quoted strings, else the bare text. */
function yamlValue(raw) {
  const v = String(raw ?? '').trim();
  if (v.startsWith('"')) {
    try {
      return String(JSON.parse(v));
    } catch {
      return v.replace(/^"|"$/g, '');
    }
  }
  return v.replace(/^'(.*)'$/, '$1');
}

function keyValues(text) {
  const data = {};
  for (const line of String(text || '').split(/\r?\n/)) {
    const kv = /^([A-Za-z_][\w-]*):\s?(.*)$/.exec(line);
    if (kv) data[kv[1]] = yamlValue(kv[2]);
  }
  return data;
}

/** A `.md` from the repository → `{ data: {id, title, order}, body }` (no front matter: all body). */
export function readDocumentFile(text) {
  const s = String(text ?? '').replace(/^\uFEFF/, '');
  const m = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/.exec(s);
  if (!m) return { data: {}, body: s.replace(/\r\n/g, '\n') };
  return { data: keyValues(m[1]), body: s.slice(m[0].length).replace(/\r\n/g, '\n').replace(/^\n/, '') };
}

export const readFolderFile = (text) => keyValues(text);

const dirOf = (p) => p.split('/').slice(0, -1).join('/');
const baseName = (p) => p.split('/').pop();

/** The title a hand-made file implies: "release-notes-ab12cd34.md" → "release notes". */
const titleFromFile = (p) => baseName(p).replace(/\.md$/i, '').replace(/-[a-z0-9]{8}$/i, '').replace(/[-_]+/g, ' ').trim() || 'Untitled';

/**
 * The documents part of the repository → a tree: every folder and document
 * with its id, name, order and parent (by where its file sits). Files
 * without an id (made by hand in git) get `newId()`; a folder without its
 * `_folder.yaml` is not an item — its contents go to the nearest known parent.
 * @param {Record<string,string>} files  repository path → text (documents/**, media excluded)
 * @returns {Map<string, {id, type, name, sortKey, parentId, file, body?}>}
 */
/** Ids from a repository are data: only the shape the app makes is taken as-is. */
const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;
const SORT_KEY = /^\d{15}$/;

/** The workspace a file's front matter names (card 4.5), as data: a malformed id is ignored. */
const workspaceOf = (data) =>
  SAFE_ID.test(data.scope || '') ? { scopeId: data.scope, ...(data.scope_name ? { scopeName: data.scope_name } : {}) } : {};

/** The id8 a file name carries: "login-flow-d1bbbbbb.md" / "specs-f1aaaaaa/_folder.yaml" → "d1bbbbbb". */
export function id8InPath(p) {
  const m = /-([a-z0-9]{8})(?:\.md|\/_folder\.yaml)$/i.exec(String(p));
  return m ? m[1].toLowerCase() : null;
}

/**
 * @param {(path: string, opts: { copy: boolean }) => string} newId  an id for a file without one (copy: a duplicate)
 * @param {{ previousPathOf?: Map<string,string> }} opts  where each id was before: on a duplicate id the
 *   file that held it keeps it and the newcomer (a copy) gets a new one — a copy never takes over the original.
 */
export function treeFromFiles(files, newId, { previousPathOf = new Map(), previousIdOfPath = new Map(), foreign = new Set() } = {}) {
  const items = [];
  const paths = Object.keys(files)
    .filter((p) => p.startsWith('documents/') && !p.startsWith(`${MEDIA_DIR}/`))
    .sort();
  for (const p of paths) {
    if (baseName(p) === '_folder.yaml') {
      const d = readFolderFile(files[p]);
      const id = SAFE_ID.test(d.id || '') ? d.id : newId(p, { copy: false });
      items.push({ id, type: 'folder', name: d.name || baseName(dirOf(p)).replace(/-[a-z0-9]{8}$/i, ''), sortKey: SORT_KEY.test(d.order || '') ? d.order : '', file: p, ...workspaceOf(d) });
    } else if (p.endsWith('.md')) {
      const { data, body } = readDocumentFile(files[p]);
      const id = SAFE_ID.test(data.id || '') ? data.id : newId(p, { copy: false });
      const tags = tagsFromFrontMatter(data.tags);
      items.push({ id, type: 'document', name: data.title ?? titleFromFile(p), sortKey: SORT_KEY.test(data.order || '') ? data.order : '', file: p, body, ...(tags.length ? { tags } : {}), ...workspaceOf(data) });
    }
  }
  ownFileIds(items, { previousPathOf, previousIdOfPath, foreign, copyId: (f) => newId(f, { copy: true }) });

  const dirOwner = new Map(); // a folder's own dir, or a page's subpage dir → its id
  for (const it of items) dirOwner.set(it.type === 'folder' ? dirOf(it.file) : it.file.replace(/\.md$/, ''), it.id);
  const out = new Map();
  for (const it of items) {
    let dir = it.type === 'folder' ? dirOf(dirOf(it.file)) : dirOf(it.file);
    let parentId = null;
    while (dir && dir !== 'documents') {
      if (dirOwner.has(dir) && dirOwner.get(dir) !== it.id) {
        parentId = dirOwner.get(dir);
        break;
      }
      dir = dirOf(dir);
    }
    out.set(it.id, { ...it, parentId });
  }
  return out;
}

/** FNV-1a over bytes or text — a cheap "did this change" mark (not security). */
export function fingerprint(value) {
  const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : value || new Uint8Array(0);
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i += 1) {
    h ^= bytes[i];
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${h.toString(16)}:${bytes.length}`;
}

/**
 * Which file owns which id, for every kind of scope item (review of S2.5–S2.7).
 * Ids in a repository are data a teammate can edit or copy:
 *   - a file whose id was EDITED keeps the id it had (when no other file
 *     claims that one now) — an edited id never deletes and re-creates an item;
 *   - on a duplicate, the file that held the id before keeps it (else the
 *     first); the others are copies and get a new id;
 *   - an id that belongs to another kind of record here (a page's id on a
 *     task, a board's on a page…) is a copy too — records share one id space,
 *     so taking it would overwrite that record.
 * @param {Array<{id, file}>} items  changed in place
 */
export function ownFileIds(items, { previousPathOf = new Map(), previousIdOfPath = new Map(), foreign = new Set(), copyId }) {
  const claimed = (id, self) => items.some((o) => o !== self && o.id === id);
  for (const it of items) {
    const had = previousIdOfPath.get(it.file);
    if (had && had !== it.id && !claimed(had, it)) it.id = had;
  }
  const holders = new Map();
  for (const it of items) {
    const held = holders.get(it.id);
    if (!held || (previousPathOf.get(it.id) === it.file && held.file !== it.file)) holders.set(it.id, it);
  }
  for (const it of items) if (holders.get(it.id) !== it || foreign.has(it.id)) it.id = copyId(it.file);
}
